<template>
  <div
    ref="rootRef"
    :class="rootClasses"
    role="separator"
    :tabindex="props.disabled ? -1 : 0"
    :aria-orientation="ariaOrientation"
    :aria-valuemin="normalizedMin"
    :aria-valuemax="normalizedMax"
    :aria-valuenow="normalizedValue"
    :aria-valuetext="props.ariaValueText"
    :aria-label="props.ariaLabel"
    :aria-labelledby="props.ariaLabelledby"
    :aria-controls="props.ariaControls"
    :aria-disabled="props.disabled ? 'true' : undefined"
    data-cy="ds-resize-handle"
    @pointerdown="onPointerDown"
    @pointermove="onPointerMove"
    @pointerup="onPointerUp"
    @pointercancel="onPointerCancel"
    @lostpointercapture="onLostPointerCapture"
    @keydown="onKeydown"
  >
    <span
      :class="DS_RESIZE_HANDLE_CLASSES.Thumb"
      data-cy="ds-resize-handle__thumb"
      aria-hidden="true"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { useDSResizeHandle } from "@/src/design_system/primitives/resize_handle/composables/useDSResizeHandle";
import {
  DS_RESIZE_HANDLE_CLASSES,
  DS_RESIZE_HANDLE_DIRECTION_TO_CLASS_MAP,
} from "@/src/design_system/primitives/resize_handle/constants/classes";
import type {
  DSResizeHandleEmits,
  DSResizeHandleResizeEndDetails,
  DSResizeHandleResizeStartDetails,
} from "@/src/design_system/primitives/resize_handle/types/emits";
import { DS_RESIZE_HANDLE_NATIVE_EVENT } from "@/src/design_system/primitives/resize_handle/types/emits";
import type {
  DSResizeHandleModelValue,
  DSResizeHandleProps,
} from "@/src/design_system/primitives/resize_handle/types/props";
import { DS_DIRECTION, type DSDirection } from "@/src/design_system/shared/types/direction";

const ARIA_ORIENTATION_BY_DIRECTION: Record<DSDirection, "horizontal" | "vertical"> = {
  [DS_DIRECTION.Horizontally]: "horizontal",
  [DS_DIRECTION.Vertically]: "vertical",
};

const props = withDefaults(defineProps<DSResizeHandleProps>(), {
  direction: DS_DIRECTION.Horizontally,
  min: 0,
  step: 1,
  disabled: false,
});
const modelValue = defineModel<DSResizeHandleModelValue>({ required: true });
const emit = defineEmits<DSResizeHandleEmits>();
const rootRef = ref<HTMLElement>();

const dispatchNativeEvent = (name: string, detail: object, cancelable = false) =>
  rootRef.value?.dispatchEvent(new CustomEvent(name, { detail, bubbles: true, cancelable, composed: true })) ?? true;

const emitResizeStart = (details: DSResizeHandleResizeStartDetails) => {
  if (!dispatchNativeEvent(DS_RESIZE_HANDLE_NATIVE_EVENT.ResizeStart, details, true)) return false;

  emit("resize-start", details);
  return true;
};

const emitResizeEnd = (details: DSResizeHandleResizeEndDetails) => {
  emit("resize-end", details);
  dispatchNativeEvent(DS_RESIZE_HANDLE_NATIVE_EVENT.ResizeEnd, details);
};

const {
  isActive,
  normalizedDirection,
  normalizedMin,
  normalizedMax,
  normalizedValue,
  onPointerDown,
  onPointerMove,
  onPointerUp,
  onPointerCancel,
  onLostPointerCapture,
  onKeydown,
} = useDSResizeHandle({
  rootRef,
  props,
  modelValue,
  onResizeStart: emitResizeStart,
  onResizeEnd: emitResizeEnd,
});

const ariaOrientation = computed(() => ARIA_ORIENTATION_BY_DIRECTION[normalizedDirection.value]);
const rootClasses = computed(() => [
  DS_RESIZE_HANDLE_CLASSES.Root,
  DS_RESIZE_HANDLE_DIRECTION_TO_CLASS_MAP[normalizedDirection.value],
  {
    [DS_RESIZE_HANDLE_CLASSES.Active]: isActive.value,
    [DS_RESIZE_HANDLE_CLASSES.Disabled]: props.disabled,
  },
]);
</script>
